import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FaSmile, FaFrown, FaMeh, FaTimes, FaSort, FaSortUp, FaSortDown, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { CommentsSection, CardTitle, CommentsTable as Table, SentimentBadge, PaginationContainer, PageButton, PageInfo } from '../styles';
import { Button } from '../../../components/common/Button';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const options = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('pt-BR', options);
};

const getSentimentIcon = (type) => {
    switch(type) {
        case 'Positivo': return <FaSmile />;
        case 'Negativo': return <FaFrown />;
        default: return <FaMeh />;
    }
};

const truncate = (str, n) => {
    return (str && str.length > n) ? str.substr(0, n - 1) + '...' : str;
};

const CommentsTable = ({ comentarios, posts, pages, filtros, filtroSentimento, setFiltroSentimento }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
    const ITEMS_PER_PAGE = 10;

    useEffect(() => {
        setCurrentPage(1);
    }, [filtros, filtroSentimento]);

    const getPostCaption = useCallback((postId) => {
        const post = posts.find(p => p.post_id == postId);
        return post && post.caption ? post.caption : `Post ID: ${postId}`;
    }, [posts]);

    const getPageName = useCallback((postId) => {
        if (!pages || pages.length === 0) return '-';
        const post = posts.find(p => p.post_id == postId);
        if (!post) return '-';
        const page = pages.find(pg => pg.page_id == post.page_id);
        return page ? page.name : '-';
    }, [posts, pages]);

    const comentariosFiltrados = useMemo(() => {
        return comentarios.filter(c => {
            const postPai = posts.find(p => p.post_id == c.post);
            let matchDate = true;
            if (postPai && (filtros.viewInicio || filtros.viewFim)) {
                 const postDate = new Date(postPai.timestamp);
                 if (filtros.viewInicio && postDate < new Date(filtros.viewInicio)) matchDate = false;
                 if (filtros.viewFim) {
                    const end = new Date(filtros.viewFim);
                    end.setHours(23, 59, 59, 999);
                    if (postDate > end) matchDate = false;
                 }
            }
            const correspondeTipo = filtros.tipo === 'todos' || (postPai && postPai.media_product_type === filtros.tipo);
            const correspondePagina = filtros.pagina === 'todas' || c.page_id == filtros.pagina;
            const correspondePost = filtros.post === 'todos' || c.post == filtros.post;
            const correspondeSentimento = !filtroSentimento || c.classification === filtroSentimento;

            return correspondePagina && correspondePost && correspondeTipo && matchDate && correspondeSentimento;
        });
    }, [comentarios, posts, filtros, filtroSentimento]);

    const comentariosOrdenados = useMemo(() => {
        let lista = [...comentariosFiltrados];
        
        const listaPreparada = lista.map(c => ({ 
            ...c, 
            post_caption: getPostCaption(c.post),
            page_name: getPageName(c.post) 
        }));

        return listaPreparada.sort((a, b) => {
            let valA, valB;
            if (sortConfig.key === 'timestamp') {
                valA = new Date(a.timestamp); valB = new Date(b.timestamp);
            } else if (sortConfig.key === 'post_name') {
                valA = a.post_caption.toLowerCase(); valB = b.post_caption.toLowerCase();
            } else if (sortConfig.key === 'page_name') { 
                valA = a.page_name.toLowerCase(); valB = b.page_name.toLowerCase();
            } else if (sortConfig.key === 'classification') {
                valA = (a.classification || '').toLowerCase(); valB = (b.classification || '').toLowerCase();
            } else {
                valA = (a.comment || '').toLowerCase(); valB = (b.comment || '').toLowerCase();
            }
            
            if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [comentariosFiltrados, sortConfig, getPostCaption, getPageName]);

    const { currentComments, totalPages } = useMemo(() => {
        const total = comentariosOrdenados.length;
        const totalPages = Math.ceil(total / ITEMS_PER_PAGE);
        const validCurrentPage = Math.max(1, Math.min(currentPage, totalPages || 1));
        const startIndex = (validCurrentPage - 1) * ITEMS_PER_PAGE;
        
        return {
            currentComments: comentariosOrdenados.slice(startIndex, startIndex + ITEMS_PER_PAGE),
            totalPages
        };
    }, [comentariosOrdenados, currentPage]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <FaSort style={{marginLeft: '5px', opacity: 0.3}} />;
        return sortConfig.direction === 'asc' ? <FaSortUp style={{marginLeft: '5px'}} /> : <FaSortDown style={{marginLeft: '5px'}} />;
    };

    return (
        <CommentsSection>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                <CardTitle style={{marginBottom: 0}}>
                    Lista de Comentários {filtroSentimento ? ` (${filtroSentimento}s - ${comentariosFiltrados.length})` : ` (Todos - ${comentariosFiltrados.length})`}
                </CardTitle>
                {filtroSentimento && (
                    <Button variant="secondary" onClick={() => setFiltroSentimento(null)} style={{fontSize: '0.8rem', padding: '0.5rem'}}>
                        <FaTimes /> Limpar Filtro
                    </Button>
                )}
            </div>

            {currentComments.length > 0 ? (
                <>
                    <Table>
                        <thead>
                            <tr>
                                {filtros.pagina === 'todas' && (
                                    <th style={{width: '15%'}} onClick={() => requestSort('page_name')}>
                                        Página {getSortIcon('page_name')}
                                    </th>
                                )}

                                <th style={{width: '25%'}} onClick={() => requestSort('post_name')}>Post {getSortIcon('post_name')}</th>
                                <th style={{width: '30%'}} onClick={() => requestSort('comment')}>Comentário {getSortIcon('comment')}</th>
                                <th style={{width: '15%'}} onClick={() => requestSort('timestamp')}>Data/Hora {getSortIcon('timestamp')}</th>
                                <th style={{width: '15%'}} onClick={() => requestSort('classification')}>Classificação {getSortIcon('classification')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentComments.map(comment => (
                                <tr key={comment.comment_id}>
                                    
                                    {filtros.pagina === 'todas' && (
                                        <td style={{fontWeight: 'bold', fontSize: '0.9em', color: '#555'}}>
                                            {comment.page_name}
                                        </td>
                                    )}

                                    <td title={comment.post_caption}><small style={{color: '#666'}}>{truncate(comment.post_caption, 40)}</small></td>
                                    <td>{comment.comment}</td>
                                    <td>{formatDate(comment.timestamp)}</td>
                                    <td>
                                        <SentimentBadge type={comment.classification}>
                                            {getSentimentIcon(comment.classification)} {comment.classification || 'Neutro'}
                                        </SentimentBadge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    {totalPages > 1 && (
                        <PaginationContainer>
                            <PageButton onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}><FaChevronLeft /> Anterior</PageButton>
                            <PageInfo>Página {currentPage} de {totalPages}</PageInfo>
                            <PageButton onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>Próximo <FaChevronRight /></PageButton>
                        </PaginationContainer>
                    )}
                </>
            ) : <p style={{padding: '1rem', textAlign: 'center', color: '#666'}}>Nenhum comentário encontrado.</p>}
        </CommentsSection>
    );
};

export default CommentsTable;